import type { ContractListFacets, ContractListItem, ContractSortField } from "@closer/shared";
import {
  CONTRACT_SORT_FIELD_NAMES,
  CONTRACT_SORT_FIELDS,
  CONTRACT_STATUS_NAMES,
} from "@closer/shared";
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
} from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { ContractIcon } from "../../components/icons";
import { StateView } from "../../components/StateView";
import { NewContractAction } from "../../layouts/NewContractAction";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { contractStatusTone } from "../../lib/contractStatusTone";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * Tüm Sözleşmeler (CLOS-115 `[083]`) + arama, süzgeç ve sıralama (CLOS-116 `[084]`).
 *
 * ### ⚠ Süzgeç SUNUCUDA
 *
 * Arşiv ayrımı istemcide yapılsaydı sunucu arşivli kayıtları yine gönderirdi ve
 * arşivlenmiş bir sözleşmenin varlığı ağ trafiğinden okunabilirdi. Kart: *"arşivlenmiş
 * kayıtların varlığı aktif liste SAYACINDAN veya filtre değerlerinden yetkisiz kullanıcıya
 * sızdırılmaz."*
 *
 * ### ⚠ Süzgeç durumu ADRESTE
 *
 * Kart: *"uygun filtre durumu KORUNABİLİR."* Yalnız bileşen state'inde tutulsaydı sayfa
 * yenilemek, geri tuşu ya da bağlantıyı bir meslektaşa göndermek süzgeci düşürürdü —
 * kullanıcı aynı daraltmayı her seferinde elle kurardı.
 *
 * ### ⚠ Çalışma alanı değişince süzgeç SIFIRLANIYOR
 *
 * Süzgeç değerleri alana özgü: bir organizasyonun etiketi ya da tür kodu ötekinde
 * anlamsız. Adres çubuğunda kalsalardı geçişten sonra kullanıcı, sebebini göremediği boş
 * bir liste görürdü.
 *
 * ### ⚠ Liste bir YETKİ DEĞİL
 *
 * Satıra tıklamak detaya götürüyor ama detay kendi kayıt erişimini yeniden doğruluyor
 * (`[079]`). Liste ile detay arasında erişim kaldırılabilir; ekran bunu bir hata değil,
 * olağan bir sonuç olarak karşılıyor.
 */

const LIMIT = 20;

const ARSIV = "archived";

/** Adreste taşınan süzgeç anahtarları — "temizle" ve alan geçişi bu listeyi siliyor. */
const SUZGEC_ANAHTARLARI = [
  "q",
  "status",
  "owner",
  "responsible",
  "type",
  "tag",
  "party",
  "endFrom",
  "endTo",
  "sort",
  "dir",
  "page",
] as const;

type SuzgecAnahtari = (typeof SUZGEC_ANAHTARLARI)[number];

function siralamaAlani(ham: string | null): ContractSortField {
  return (CONTRACT_SORT_FIELDS as readonly string[]).includes(ham ?? "")
    ? (ham as ContractSortField)
    : "updatedAt";
}

export function ContractsPage() {
  const { activeTenantId, can } = useAuth();
  /** Rota `AppLayout` altında: aktif çalışma alanı olmadan buraya gelinmiyor. */
  const tenantId = activeTenantId!;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  /** Ayrıntılı süzgeçler isteğe bağlıdır; listenin ilk görünümünü kaplamaz. */
  const [panelAcik, setPanelAcik] = useState(false);

  /**
   * ⚠ Süzgeçler HANGİ ALANA ait olduklarını adreste taşıyor. Alan değiştiğinde adres
   * temizleniyor; temizlik efekt içinde olduğu için o kare boyunca süzgeçler ZATEN YOK
   * sayılıyor (`alanUyusuyor`). Yalnız efekte güvenseydik, geçişin ilk karesinde yeni alan
   * eski alanın süzgeciyle sorgulanır ve kullanıcı sebebini göremediği boş bir liste
   * görürdü.
   */
  const alanUyusuyor = params.get("alan") === tenantId;

  useEffect(() => {
    if (alanUyusuyor) return;
    const temiz = new URLSearchParams(window.location.search);
    for (const anahtar of SUZGEC_ANAHTARLARI) temiz.delete(anahtar);
    temiz.set("alan", tenantId);
    setParams(temiz, { replace: true });
  }, [alanUyusuyor, tenantId, setParams]);

  function oku(ad: SuzgecAnahtari): string {
    return alanUyusuyor ? (params.get(ad) ?? "") : "";
  }

  const page = Number(oku("page") || "1") || 1;
  const q = oku("q");
  const status = oku("status");
  const owner = oku("owner");
  /** "Sorumlusu olduklarım" (CLOS-1126 · K5): değer yalnız `me`; kimlik sunucuda çözülür. */
  const responsible = oku("responsible") === "me" ? "me" : "";
  const type = oku("type");
  const tag = oku("tag");
  const party = oku("party");
  const endFrom = oku("endFrom");
  const endTo = oku("endTo");
  const sort = siralamaAlani(oku("sort"));
  const dir = oku("dir") === "asc" ? "asc" : "desc";

  const uygulananlar = [q, status, type, owner, responsible, tag, party, endFrom, endTo].filter(
    Boolean,
  ).length;

  /** Süzgeç değişince İLK SAYFAYA: 3. sayfada kalmak boş bir liste gösterirdi. */
  function suzgecYaz(degisiklikler: Partial<Record<SuzgecAnahtari, string>>) {
    const sonraki = new URLSearchParams(params);
    for (const [anahtar, deger] of Object.entries(degisiklikler)) {
      if (deger) sonraki.set(anahtar, deger);
      else sonraki.delete(anahtar);
    }
    if (!("page" in degisiklikler)) sonraki.delete("page");
    setParams(sonraki);
  }

  function temizle() {
    const sonraki = new URLSearchParams(params);
    for (const anahtar of SUZGEC_ANAHTARLARI) sonraki.delete(anahtar);
    setParams(sonraki);
  }

  function aramaGonder(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const veri = new FormData(e.currentTarget);
    suzgecYaz({ q: String(veri.get("q") ?? "").trim() });
  }

  const sorgu = useQuery({
    queryKey: tenantKey(
      tenantId,
      "contracts",
      page,
      status,
      owner,
      responsible,
      q,
      type,
      tag,
      party,
      endFrom,
      endTo,
      sort,
      dir,
    ),
    queryFn: async () => {
      const arama = new URLSearchParams({
        page: String(page),
        limit: String(LIMIT),
        sort,
        dir,
      });
      for (const [ad, deger] of Object.entries({
        status,
        q,
        type,
        owner,
        responsible,
        tag,
        party,
        endFrom,
        endTo,
      })) {
        if (deger) arama.set(ad, deger);
      }
      return api<{ items: ContractListItem[]; facets: ContractListFacets }>(
        `/api/contracts?${arama}`,
        { tenantId },
      );
    },
  });

  const facets = sorgu.data?.data.facets;

  const columns: ColumnDef<ContractListItem>[] = [
    {
      accessorKey: "title",
      header: "Başlık",
      /**
       * ⚠ Detaya geçiş SÜZGEÇ BAĞLAMINI taşıyor (`liste`): kullanıcı detaydan döndüğünde
       * aynı daraltılmış listeyi buluyor. Taşımasaydı her detay ziyareti süzgeci sıfırlar
       * ve kullanıcı aramayı baştan kurardı.
       *
       * ⚠ Bağlantı bir YETKİ DEĞİL: detay kendi kayıt erişimini yeniden doğruluyor
       * (`[079]`). Listede görünmüş olmak, açılabilir olmak anlamına gelmiyor.
       */
      cell: ({ row }) => (
        <Link
          className="flex items-center gap-3 font-medium text-fg-heading hover:text-primary hover:underline"
          to={`/app/sozlesmeler/${row.original.id}?liste=${encodeURIComponent(params.toString() ? `?${params}` : "")}`}
        >
          <span
            aria-hidden="true"
            className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary"
          >
            <ContractIcon className="size-[18px]" />
          </span>
          <span className="min-w-0 truncate">{row.original.title}</span>
        </Link>
      ),
    },
    {
      accessorKey: "status",
      header: "Durum",
      /**
       * ⚠ Ton ORTAK EŞLEMEDEN (`lib/contractStatusTone`). Burada "aktifse yeşil, değilse
       * gri" yazılıydı: dokuz farklı yaşam döngüsü durumu tek bir griye düşüyor ve renk
       * hiçbir şey ayırt etmiyordu. Detay ekranı da kendi ayrımını yazmıştı; aynı sözleşme
       * iki ekranda iki renkte görünüyordu.
       */
      cell: ({ row }) => (
        <Badge tone={contractStatusTone(row.original.status)}>
          {CONTRACT_STATUS_NAMES[row.original.status as keyof typeof CONTRACT_STATUS_NAMES] ??
            row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: "ownerName",
      header: "Sorumlu",
      /** Sorumlu YOKSA boş değil açık bir metin: boş hücre "yüklenmedi mi?" sorusunu doğurur. */
      cell: ({ row }) =>
        row.original.ownerName ? (
          <span className="flex items-center gap-2">
            <Avatar name={row.original.ownerName} size="sm" />
            <span className="truncate">{row.original.ownerName}</span>
          </span>
        ) : (
          "—"
        ),
    },
    {
      accessorKey: "endDate",
      header: "Bitiş",
      cell: ({ row }) =>
        row.original.endDate ? new Date(row.original.endDate).toLocaleDateString("tr-TR") : "—",
    },
    { accessorKey: "partyCount", header: "Taraf" },
    {
      accessorKey: "updatedAt",
      header: "Son değişiklik",
      cell: ({ row }) => new Date(row.original.updatedAt).toLocaleDateString("tr-TR"),
    },
  ];

  if (sorgu.isError) {
    return (
      <StateView state={resolveErrorState(sorgu.error)} onRetry={() => void sorgu.refetch()} />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Tüm Sözleşmeler"
        description="Erişiminiz olan tüm sözleşmeleri görüntüleyin, filtreleyin ve yönetin."
      />

      <div className="flex flex-wrap items-center gap-2">
        {/* Arama alanı satırın tamamını alıyor — onaylı tasarımda liste başının ana kontrolü. */}
        <form
          onSubmit={aramaGonder}
          className="flex min-w-64 flex-1 items-center gap-2"
          role="search"
        >
          {/*
            ⚠ Arama kutusu ADRESTEKİ değerle yeniden kuruluyor (`key`): "temizle" ya da alan
            geçişi adresi sildiğinde kutuda eski metnin kalması, kullanıcıya uygulanmayan bir
            süzgeç gösterirdi.
          */}
          <Input
            key={q}
            name="q"
            defaultValue={q}
            type="search"
            aria-label="Başlık veya referansta ara"
            placeholder="Sözleşme adı, taraf, numara ara…"
            className="min-w-0 flex-1"
          />
          <Button type="submit" variant="secondary">
            Ara
          </Button>
        </form>

        {/* Tek filtre giriş noktası, mobil ve masaüstünde aynı davranır. */}
        <Button
          type="button"
          variant="secondary"
          aria-expanded={panelAcik}
          aria-controls="suzgec-paneli"
          onClick={() => setPanelAcik((a) => !a)}
        >
          Süzgeçler{uygulananlar > 0 ? ` (${uygulananlar})` : ""}
        </Button>
      </div>

      <div
        id="suzgec-paneli"
        className={`${panelAcik ? "grid" : "hidden"} grid-cols-1 gap-4 rounded-xl border border-border-subtle bg-surface p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-4`}
      >
        {/*
          ⚠ Etiket ile kontrol `htmlFor`/`id` ile eşleşiyor, kontrol etiketin İÇİNDE
          sarmalanmıyor. Sarmalandığında erişilebilir ad, seçili seçeneğin metnini de
          yutuyor ("DurumArşiv hariç tümü") ve "Durum" araması Sıralama kutusuna da
          çarpıyordu — Sıralama seçeneklerinden biri "Durum" olduğu için. Uçtan uca test
          bunu kesin bir hatayla bildirdi; ekran okuyucu ise sessizce yanlış okurdu.
        */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-durum">
            Durum
          </label>
          <Select
            id="suzgec-durum"
            value={status}
            onChange={(e) => suzgecYaz({ status: e.target.value })}
          >
            <option value="">Arşiv hariç tümü</option>
            {/*
              ⚠ Seçenekler SAYAÇTAN: kart *"yalnız kullanıcının mevcut sonuç kümesinde
              görebildiği değerler filtre seçeneği ve sayaç olarak gösterilir"* diyor. Sabit
              bir liste, hiç sözleşmesi olmayan kullanıcıya bile hangi durumların
              kullanıldığını söylerdi.
            */}
            {(facets?.status ?? []).map((f) => (
              <option key={f.value} value={f.value}>
                {f.label} ({f.count})
              </option>
            ))}
            {/*
              ⚠ ARŞİV her zaman seçilebilir olmalı. Seçenekler yalnız sayaçtan gelseydi arşiv
              hiç listelenmezdi — çünkü varsayılan liste onu zaten hariç tutuyor — ve
              kullanıcı arşive arayüzden ASLA ulaşamazdı (`[083]`: *"arşiv kayıtları uygun
              filtre ile görülebilir"*). Uçtan uca test bunu yakaladı: açılırda "archived"
              seçeneği yoktu.

              ⚠ Bu seçenek SAYI TAŞIMIYOR. Sayı, kullanıcının henüz göremediği kayıtların
              varlığını ele verirdi; seçeneğin kendisi ise yalnız zaten hakkı olan bir görünümü
              öneriyor.
            */}
            {!(facets?.status ?? []).some((f) => f.value === ARSIV) && (
              <option value={ARSIV}>{CONTRACT_STATUS_NAMES.archived}</option>
            )}
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-tur">
            Tür
          </label>
          <Select
            id="suzgec-tur"
            value={type}
            onChange={(e) => suzgecYaz({ type: e.target.value })}
          >
            <option value="">Tümü</option>
            {(facets?.type ?? []).map((f) => (
              <option key={f.value} value={f.value}>
                {f.label} ({f.count})
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-sorumlu">
            Sorumlu
          </label>
          <Select
            id="suzgec-sorumlu"
            value={owner}
            onChange={(e) => suzgecYaz({ owner: e.target.value })}
          >
            <option value="">Tümü</option>
            {/*
              ⚠ Seçenekler SAYAÇTAN, üye dizininden değil. Dizinden gelseydi hiçbir
              sözleşmesi görünmeyen kullanıcı bile organizasyonda kimlerin olduğunu
              süzgeçten okurdu. Değer ÜYELİK kimliği; sorumluluk kişiye değil üyeliğe bağlı.
            */}
            {(facets?.owner ?? []).map((f) => (
              <option key={f.value} value={f.value}>
                {f.label} ({f.count})
              </option>
            ))}
          </Select>
        </div>
        {/*
          "SORUMLUSU OLDUKLARIM" (CLOS-1126 · Halit K5): owner + alan + genel katkı satırlarım.
          Değer `me`; üyelik kimliği istemciden gitmez. Departman üyeliği kişiyi sokmaz.
        */}
        <div className="flex flex-col justify-end gap-1">
          <Checkbox
            label="Sorumlusu olduklarım"
            checked={responsible === "me"}
            onChange={(e) => suzgecYaz({ responsible: e.target.checked ? "me" : "" })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-taraf">
            Taraf
          </label>
          <Input
            id="suzgec-taraf"
            key={`party-${party}`}
            defaultValue={party}
            placeholder="Taraf adı"
            onBlur={(e) => {
              if (e.target.value.trim() !== party) suzgecYaz({ party: e.target.value.trim() });
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-etiket">
            Etiket
          </label>
          <Input
            id="suzgec-etiket"
            key={`tag-${tag}`}
            defaultValue={tag}
            placeholder="Etiket"
            onBlur={(e) => {
              if (e.target.value.trim() !== tag) suzgecYaz({ tag: e.target.value.trim() });
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-bitis-baslangic">
            Bitiş başlangıcı
          </label>
          <Input
            id="suzgec-bitis-baslangic"
            type="date"
            value={endFrom}
            onChange={(e) => suzgecYaz({ endFrom: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-bitis-son">
            Bitiş sonu
          </label>
          <Input
            id="suzgec-bitis-son"
            type="date"
            value={endTo}
            onChange={(e) => suzgecYaz({ endTo: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-siralama">
            Sıralama
          </label>
          <Select
            id="suzgec-siralama"
            value={sort}
            onChange={(e) => suzgecYaz({ sort: e.target.value })}
          >
            {CONTRACT_SORT_FIELDS.map((alan) => (
              <option key={alan} value={alan}>
                {CONTRACT_SORT_FIELD_NAMES[alan]}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-yon">
            Yön
          </label>
          <Select id="suzgec-yon" value={dir} onChange={(e) => suzgecYaz({ dir: e.target.value })}>
            <option value="desc">Azalan</option>
            <option value="asc">Artan</option>
          </Select>
        </div>

        <div className="flex items-end">
          <Button type="button" variant="ghost" onClick={temizle} disabled={uygulananlar === 0}>
            Süzgeçleri temizle
          </Button>
        </div>
      </div>

      {sorgu.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <Table
          columns={columns}
          data={sorgu.data?.data.items ?? []}
          pagination={{
            page,
            limit: LIMIT,
            total: sorgu.data?.meta?.total ?? 0,
            onPageChange: (p) => suzgecYaz({ page: String(p) }),
          }}
          /**
           * ⚠ Boş durumda "Süzgeçleri temizle" AKSİYONU EKLENMEDİ: aynı adla filtre
           * panelinde zaten bir düğme var ve panel bu durumda da (uygulananlar > 0)
           * görünür kalıyor — ikinci bir aynı-adlı düğme testin
           * `getByRole("button", { name: "Süzgeçleri temizle" })` tekil eşleşmesini
           * belirsizleştirirdi.
           */
          /**
           * ⚠ İKİ AYRI BOŞLUK, İKİ AYRI CÜMLE. Tek metin kullanıldığında hiç sözleşmesi
           * olmayan yeni kullanıcı, hiçbir süzgeç uygulamadığı hâlde "Bu filtreye uyan
           * sözleşme bulunmuyor." cümlesiyle karşılaşıyor ve olmayan bir süzgeci
           * aramaya başlıyordu. Müvekkil listesi bu ayrımı zaten yapıyor; iki liste
           * arasındaki fark da kendi başına bir tutarsızlıktı.
           */
          empty={
            uygulananlar > 0 ? (
              <EmptyState icon={<ContractIcon />} title="Bu filtreye uyan sözleşme bulunmuyor." />
            ) : (
              /*
                ⚠ BOŞ DURUM YETKİ FARKINDA (CLOS-1279 · matris 12).

                Eskiden açıklama "üst bardaki Yeni Sözleşme ile oluşturabilirsiniz" diyordu.
                Ama o düğme `can("contract.create")` ile gizleniyor: yetkisi olmayan
                kullanıcıya ekranında VAR OLMAYAN bir düğme tarif ediliyordu — kullanıcı
                olmayan bir şeyi arıyordu.

                ⚠ Aksiyon olarak `NewContractAction` YENİDEN KULLANILIYOR, yeni bir düğme
                yazılmıyor: yetki kapısı, oluşturma engelleri (kota/abonelik) ve modal zaten
                onun içinde. Kopya yazsaydık iki ayrı kapı doğar ve biri gün gelip
                diğerinden ayrışırdı.
              */
              <EmptyState
                icon={<ContractIcon />}
                title="Henüz sözleşme yok."
                description={
                  can("contract.create")
                    ? "İlk sözleşmenizi oluşturarak başlayın."
                    : "Size bir sözleşme paylaşıldığında burada görünür."
                }
                action={can("contract.create") ? <NewContractAction /> : undefined}
              />
            )
          }
        />
      )}

      {/*
        Satıra tıklamak yerine ayrı bir bağlantı sütunu yok: tablo bileşeni satır tıklaması
        desteklemiyor ve sayfaya özel bir tablo yazmak, `packages/ui` kuralını delerdi.
        Detaya geçiş detay ekranı kartıyla birlikte tabloya eklenecek.
      */}
      <p className="text-sm text-fg-soft">
        Toplam {sorgu.data?.meta?.total ?? 0} sözleşme.{" "}
        <Button
          variant="ghost"
          onClick={() => void navigate("/app")}
          className="!inline-flex !h-auto !px-1 !py-0 align-baseline font-medium !text-accent hover:underline"
        >
          Ana sayfaya dön
        </Button>
      </p>
    </div>
  );
}
